import React, { useCallback, useEffect, useRef, useState } from "react";
import { KeyPress } from "@att-bit/duc.utils.key-press";
import { AnalyticsEvent } from '@att-bit/ui-frameworks.components.analytics-event';
import { docComplete } from '@att-bit/duc.utils.types';

export type TooltipProps = {
  /** Any additional styles to add to the tooltip text box. */
  boxWidth?: number;
  /** Tooltip trigger element */
  children: React.ReactNode;
  /** Location of tooltip defaults to center. To move right, enter a positive number, to move left, enter a negative number. If number is greater than the size of the box, it defaults to center */
  offset?: number;
  /** Location tooltip box is placed relative to item: top | bottom. Component will change the postion from top to bottom vise versa if there is not enough room*/
  position?: TooltipPosition;
  /**  Legal color for onDark or not */
  variant?: TooltipVariant;
  /** styles to be passed */
  style?: object;
  /** Any additional className to add to this tooltip. */
  className?: string;
  /** To set the additional information through aria-label about tooltip. */
  helpText?: string;
  /** removes z-index from rel class by adding new rel-pmt class */
  zIndex?: boolean;
  /** Used for unique  identification of element. */
  id: string;
  /** used for child container of tooltip */
  styleOfChildContainer?: object;
  /** sets data for analytics (please include linkName in the data, by default it is "tooltip") */
  analyticsData? : any
  /** Allows tooltip to send analytics data if true */
  tooltipAnalytics?: boolean
  /** position of the tooltip is changed depends on the real estate, but the app dosen't want it to be changed for some cases. they can use this prop to force it */
  forcePosition?: boolean
};
export enum TooltipPosition {
  Top = "top",
  Bottom = "bottom",
}

export enum TooltipVariant {
  ON_DARK = "bg-gray-200 color-gray-800",
  default = "",
}

export const Tooltip = ({
  boxWidth,
  position,
  children,
  variant,
  offset,
  style,
  className,
  helpText,
  zIndex,
  styleOfChildContainer,
  analyticsData,
  tooltipAnalytics=false,
  forcePosition =  false,
  ...props
}: TooltipProps) => {
  const [showState, setShowState] = useState(false);
  const [positionState, setPositionState] = useState(position);
  const [isClick, setIsClick] = useState(false);
  const [content, setContent] = useState([]);
  const [isHover, setIsHover] = useState(false);
  const [isFocusable, setIsFocusable] = useState(true);
  const arrowRef = useRef<HTMLDivElement>(null);
  const boxRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  const showClassName = showState ? " show" : "";
  const arrowClassName = `${variant === TooltipVariant.ON_DARK ? "arrowonDark" : "arrow"} position-top ${variant} ${showClassName}`;

  const msgid: string = `msg-${props["id"]}`;
  const boxClassName = `position-top info-box radius-sm box-shadow2 ${variant}${showClassName}`;

  useEffect(() => {
        const content = [];
    React.Children.map(children, (child, idx) => {
      // istanbul ignore else
      if (React.isValidElement(child) && child.type === Tooltip.Content) {
        content.push(child);
      }
    });

    setContent(content);
    window.addEventListener("resize", checkPosition);
    //istanbul ignore next
    return () => {
            suppressFocus();
      window.removeEventListener("resize", checkPosition);
    };
  }, []);

  useEffect(() => {
    if (content.length) {
      suppressFocus();
      setTimeout (() => {
        checkPosition();
      }, 100 );
    }
  }, []);

  useEffect(() => {
    if (content.length) {
      suppressFocus();
      setTimeout (() => {
        checkPosition();
      }, 100 );
    }
  }, [content]);

  const checkPosition = useCallback(() => {
        // if width of box exceeds screen size, set it responsively to the size of the screen
    const currentBox = boxRef.current;
    const currentArrow = arrowRef.current;
    const currentTrigger = triggerRef.current;
    if (!currentBox || !currentArrow || !currentTrigger) {
      return;
    }
    const bounds = currentBox.getBoundingClientRect();
    const arrowBounds = currentArrow.getBoundingClientRect();
    const triggerBounds = currentTrigger.getBoundingClientRect();
    
    // if the info icon is out of viewport
    if (!(triggerBounds.top >= 0 && triggerBounds.left >= 0 &&
        triggerBounds.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
        triggerBounds.right <= (window.innerWidth || document.documentElement.clientWidth))) {
      return;
    }
    // if it is the size of the screen
    const sidePadding = window.innerWidth > 600 ? 32 : 16;
    // vertical positioning
    //istanbul ignore else
    if (forcePosition) {
      setPositionState(position);
    } else {
      if (position === TooltipPosition.Top && bounds.top < sidePadding) {
                setPositionState(TooltipPosition.Bottom);
      } else if (
        position === TooltipPosition.Bottom &&
        bounds.bottom > window.innerHeight - sidePadding
      ) {
                setPositionState(TooltipPosition.Top);
      }
    }
    
    //istanbul ignore else
    if (
      bounds.width >=
      document.documentElement.clientWidth - sidePadding * 2
    ) {
            currentBox.style.width =
        document.documentElement.clientWidth - (sidePadding + 10) * 2 + "px";
    } else {
            currentBox.style.width = bounds.width + "px";
    }
    //istanbul ignore
    if (bounds.left < sidePadding) {
            currentBox.style.left =
        sidePadding +
        Math.abs(
          arrowBounds.left +
            8 -
            parseInt(currentBox.style.width.slice(0, -2), 10) / 2
        ) +
        triggerBounds.width / 2 +
        "px";
      if (
        currentBox.getBoundingClientRect().left +
          currentBox.getBoundingClientRect().width >=
        document.documentElement.clientWidth - sidePadding
      ) {
        const tmpLeft = parseInt(currentBox.style.left.slice(0, -2), 10);
        currentBox.style.left =
          tmpLeft -
          (currentBox.getBoundingClientRect().right -
            (document.documentElement.clientWidth - sidePadding)) +
          "px";
      }
    }
    //istanbul ignore
    if (bounds.right > document.documentElement.clientWidth - sidePadding) {
            const extra =
        arrowBounds.left +
        6 +
        parseInt(currentBox.style.width.slice(0, -2), 10) / 2 -
        (document.documentElement.clientWidth - sidePadding);
      currentBox.style.left = -extra + "px";
      if (currentBox.getBoundingClientRect().left < sidePadding) {
                const tmpLeft = parseInt(currentBox.style.left.slice(0, -2), 10);
        currentBox.style.left =
          tmpLeft +
          (sidePadding - currentBox.getBoundingClientRect().left) +
          "px";
      }
    }
    //istanbul ignore
    if (
      // is there an offset? Is that offset less than half the width of the box? If I relocate it, will the offset push me off the page?
      offset &&
      Math.abs(offset) < currentBox.getBoundingClientRect().width / 2 - 15 &&
      currentBox.getBoundingClientRect().left + offset > sidePadding &&
      currentBox.getBoundingClientRect().left +
        currentBox.getBoundingClientRect().width +
        offset <
        window.innerWidth - sidePadding
    ) {
            currentBox.style.left = "calc(50% + " + offset + "px)";
      // make sure its not pushhing off screen
      //istanbul ignore
      if (
        currentBox.getBoundingClientRect().left +
          currentBox.getBoundingClientRect().width >=
        window.innerWidth - sidePadding
      ) {
        const extra =
          arrowBounds.left +
          6 +
          parseInt(currentBox.style.width.slice(0, -2), 10) / 2 -
          (document.documentElement.clientWidth - sidePadding);
        currentBox.style.left = -extra + "px";
      } else if (currentBox.getBoundingClientRect().left < sidePadding) {
        currentBox.style.left =
          sidePadding +
          Math.abs(
            arrowBounds.left +
              6 -
              parseInt(currentBox.style.width.slice(0, -2), 10) / 2
          ) +
          triggerBounds.width / 2 +
          "px";
      }
    }
  }, []);

  const suppressFocus = () => {
    if (triggerRef.current) {
      const items = triggerRef.current!.querySelectorAll(
        "input, button, a, select, textarea"
      );
      setIsFocusable(!(items.length > 0));
    } else {
      setIsFocusable(true);
    }
  };

  const KeyDownHandler = (e: React.KeyboardEvent<HTMLElement>) => {
    if (KeyPress.escIsPressed(e)) {
      hideTooltip();
      setIsClick(false);
      setIsHover(false);
    } else if (KeyPress.enterIsPressed(e) || KeyPress.spaceIsPressed(e)) {
      e.preventDefault();
      if (!showState) {
        showTooltip();
        setIsClick(true);
        setIsHover(false);
        if(tooltipAnalytics){
          sendLnkClick();
        }
      } else {
        hideTooltip();
        setIsClick(false);
        setIsHover(false);
      }
    }
  };

  const sendLnkClick = () => {
    const lnkname = analyticsData?.linkName != undefined ? analyticsData.linkName : "tooltip";
    const destUrl = analyticsData?.linkDestinationUrl != undefined ? analyticsData.linkDestinationUrl : "tooltip";
    const lnkPos = analyticsData?.linkPosition != undefined ? analyticsData.linkPosition : 'Body';
    const data2 = JSON.parse(JSON.stringify(analyticsData))

    if (data2) {
        delete data2.linkName;
        delete data2.linkDestinationUrl;
        delete data2.linkPosition;
    }
    
    const data = {
        linkDestinationUrl: destUrl,
        linkName: lnkname,
        linkPosition: lnkPos,
        ...data2
    }
    const lnkClickObj = {
      "eventCode": "Link_Click",
      "eventAction": "linkClick",
      "additionalData": data
    };
    docComplete(()=>{
      AnalyticsEvent(lnkClickObj);
    });
    console.log(data)
  }

  const onHoverOut = () => {
    if (!isClick) {
      hideTooltip();
      setIsHover(false);
    }
  };

  const onHoverIn = (e : React.MouseEvent<HTMLDivElement>) => {
    setIsHover(true);
    showTooltip();
    if(tooltipAnalytics){
      sendLnkClick();
    }
  };

  const onBlur = () => {
    setIsClick(false);
    setIsHover(false);
    hideTooltip();
  };

   const onClick = (e: any) => {
    e.stopPropagation();
    if(isClick){
      hideTooltip()
       setIsClick(false)
     }
     else{
   showTooltip()
   setIsClick(true)
     }
  };

  const showTooltip = () => {
    checkPosition();
    setShowState(true);
  };

  const hideTooltip = () => {
    setShowState(false);
  };

  return (
    <>
      <div
        className="rel inline-block"
        style={{ verticalAlign: "middle" }}
        data-testid={props["data-testId"]}
      >
        <div
          className={`${className || ""}`}
          style={style}
          data-testid={props["data-testId"]}
          id={props["id"]}
        >
          <PositionTop
            bgColor={variant}
            boxWidth={boxWidth}
            boxRef={boxRef}
            arrowRef={arrowRef}
            boxClassName={boxClassName}
            arrowClassName={arrowClassName}
            content={content}
            zIndex={zIndex}
            positionState={positionState}
            variant={variant}
            displayTooltip={showState}
          />
          <span className="hidden-spoken" id={msgid} role="tooltip">
            {content}
          </span>
          <div
            tabIndex={isFocusable ? 0 : -1}
            id={`child-container-${props.id}`}
            ref={triggerRef}
            className="rel trigger child-container"
            onClick={onClick}
            onMouseEnter={onHoverIn}
            onMouseLeave={onHoverOut}
            onBlur={onBlur}
            onKeyDown={KeyDownHandler}
            style={styleOfChildContainer}
            role="button"
            aria-describedby={msgid}
            aria-label={helpText || "Help"}
            aria-expanded={showState ? true : false}
          >
            {React.Children.map(children, (child, idx) => {
              // istanbul ignore else
              if (
                React.isValidElement(child) &&
                child.type !== Tooltip.Content
              ) {
                return React.cloneElement(child as React.ReactElement<any>);
              }
            })}
          </div>
          <PositionBottom
            bgColor={variant}
            boxWidth={boxWidth}
            boxRef={boxRef}
            arrowRef={arrowRef}
            boxClassName={boxClassName}
            arrowClassName={arrowClassName}
            content={content}
            zIndex={zIndex}
            positionState={positionState}
            variant={variant}
            displayTooltip={showState}
          />
        </div>
      </div>
      <style global jsx>{`
        .child-container {
          line-height: 0.8;
          top: -1px;
        }
        .child-container > * {
          position: relative !important;
        }
        .child-container svg {
          vertical-align: baseline;
        }
        .text-info {
          color: #fff;
          font-size: 1.2rem;
          font-family: font-regular;
        }
        
        .info-box {
          visibility: hidden;
          background-color: #1D2329;
          position: absolute;
          z-index: 1000;
          max-width: 320px;
        }
        .info-box.bg-gray-200{
          background-color: #f3f4f6;
        }
        .arrow {
          visibility: hidden;
          border-radius: 0 2px 0 2px;
          background-color: #1D2329;
          width: 8px;
          height: 8px;
          z-index: 1000;
          position: absolute;
        }
        .arrowonDark {
          visibility: hidden;
          border-radius: 0 2px 0 2px;
          width: 8px;
          height: 8px;
          z-index: 1000;
          position: absolute;
        }
        .show {
          visibility: visible !important;
        }
        .position-bottom.arrow {
          left: calc(50% - 4.2px);
          transform: translateY(3px) rotate(-45deg);
          top: 100%;
        }
        .position-bottom.arrowonDark {
          left: calc(50% - 4.2px);
          transform: translateY(3px) rotate(-45deg);
          top: 100%;
        }
        .position-bottom.info-box {
          transform: translate(-50%, 7px);
          left: 50%;
        }
        .position-top.arrow {
          left: calc(50% - 4.2px);
          transform: translateY(-5px) rotate(-45deg);
          bottom: 100%;
        }
        .position-top.arrowonDark {
          left: calc(50% - 4.2px);
          transform: translateY(-5px) rotate(-45deg);
          bottom: 100%;
        }
        .position-top.info-box {
          transform: translate(-50%, -9px);
          bottom: 100%;
          left: 50%;
        }
        .rel-pmt {
          position: relative !important;
        }
        :global(.form-row.rel) {
          z-index: auto;
        }
        :global(.is-tabbing .trigger:focus) {
          outline: 1px dotted currentColor;
          outline-offset: 1px;
        }
      `}</style>
    </>
  );
};

Tooltip.defaultProps = {
  position: TooltipPosition.Top,
  variant: TooltipVariant.default,
  zIndex: true,
};

const Content = (props) => {
  return React.Children.map(props.children, (child, idx) => {
    return child;
  });
};
Tooltip.Content = Content;

const PositionTop = React.forwardRef((props: any, ref) => {
  const {
    boxWidth,
    boxClassName,
    arrowClassName,
    boxRef,
    arrowRef,
    bgColor,
    content,
    zIndex,
    positionState,
    variant,
    displayTooltip
  } = props;

  if (positionState !== TooltipPosition.Top) return null;
  return (
    <div className={`${!displayTooltip ? "overflow-hidden " : "" } ${zIndex ? "rel-pmt " : "rel"}`}>
      <Box
        boxRef={boxRef}
        className={boxClassName}
        addtnlStyle={boxWidth ? { width: boxWidth + 'px' } : {width: 'max-content'}}
        bgColor={bgColor}
        variant={variant}
      >
        {content}
      </Box>
      <Arrow arrowRef={arrowRef} className={arrowClassName} />
    </div>
  );
});

const PositionBottom = React.forwardRef((props: any, ref) => {
  const {
    boxWidth,
    boxClassName,
    arrowClassName,
    boxRef,
    arrowRef,
    bgColor,
    content,
    zIndex,
    positionState,
    variant,
    displayTooltip
  } = props;

  if (positionState !== TooltipPosition.Bottom) return null;
  return (
    <div className={`${!displayTooltip ? "overflow-hidden " : "" } ${zIndex ? "rel-pmt " : "rel"}`}>
      <Arrow
        arrowRef={arrowRef}
        className={arrowClassName.replace("position-top", "position-bottom")}
      />
      <Box
        boxRef={boxRef}
        className={boxClassName.replace("position-top", "position-bottom")}
        addtnlStyle={boxWidth ? { width: boxWidth + 'px' } : {width: 'max-content'}}
        bgColor={bgColor}
        variant={variant}
      >
        {content}
      </Box>
    </div>
  );
});

const Arrow = React.forwardRef(({ arrowRef, className }: any, ref) => (
  <div ref={arrowRef} className={className} />
));
const Box = React.forwardRef((props: any, ref) => {
  const { boxRef, className, addtnlStyle, bgColor, children,variant } = props;
  return (
    <div ref={boxRef} style={addtnlStyle} className={className}>
      <div
        className={`mar-b-none infoText pad-t-xxs pad-b-xxs pad-l-xs pad-r-xs ${variant === TooltipVariant.ON_DARK ? "type-12" : "text-info"}`}
      >
        {children}
      </div>
    </div>
  );
});
